import { useTranslation } from 'react-i18next';
import { useDeviceDetect } from '../hooks/useDeviceDetect';
import { Download, Smartphone, Apple, Laptop, Monitor, ChevronDown } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { buildPath } from '../routing';
import { buildDownloadUrl, DOWNLOAD_FILES, getPrimaryDownloadUrl } from '../downloads';
import { normalizeLanguage } from '../i18n/utils';
import { trackConversion } from '../analytics';

interface SmartDownloadButtonProps {
  variant?: 'primary' | 'secondary';
  className?: string;
  showAllVersions?: boolean;
  buttonStyle?: React.CSSProperties;
}

export function SmartDownloadButton({ 
  variant = 'primary', 
  className = '',
  showAllVersions = false,
  buttonStyle,
}: SmartDownloadButtonProps) {
  const { t, i18n } = useTranslation();
  const { os, isCompatible, isMobile } = useDeviceDetect();
  const [showLinuxOptions, setShowLinuxOptions] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const currentLang = normalizeLanguage(i18n.language);
  const downloadPagePath = buildPath(currentLang, 'download');

  const downloadAnchors: Record<string, string> = {
    windows: `${downloadPagePath}#download-windows`,
    macos: `${downloadPagePath}#download-macos`,
    linux: `${downloadPagePath}#download-linux`,
  };
  const downloadTitles: Record<string, string> = {
    windows: t('download.windowsTitle'),
    macos: t('download.macosTitle'),
    linux: t('download.linuxTitle'),
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowLinuxOptions(false);
        setShowAllOptions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getButtonText = () => {
    if (isMobile) return t('download.mobile');
    
    switch (os) {
      case 'windows':
        return t('download.windows');
      case 'macos':
        return t('download.macos');
      case 'linux':
        return t('download.linux');
      default:
        return t('download.unknown');
    }
  };

  const getIcon = () => {
    if (isMobile) return <Smartphone className="size-5" />;
    switch (os) {
      case 'macos':
        return <Apple className="size-5" />;
      case 'linux':
        return <Laptop className="size-5" />;
      default:
        return <Download className="size-5" />;
    }
  };

  const baseStyles = "px-8 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 relative";
  
  const variantStyles = variant === 'primary'
    ? "bg-teal-600 hover:bg-teal-700 text-white"
    : "bg-white hover:bg-gray-50 text-teal-600 border-2 border-teal-600";

  const disabledStyles = !isCompatible ? "opacity-75 cursor-not-allowed" : "";

  // Dla Linux - przycisk z toggle (dropdown)
  if (os === 'linux' && !showAllVersions) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button 
          className={`${baseStyles} ${variantStyles} ${disabledStyles} ${className}`}
          onClick={() => setShowLinuxOptions(!showLinuxOptions)}
          style={buttonStyle}
        >
          {getIcon()}
          {getButtonText()}
          <ChevronDown className={`size-4 ml-1 transition-transform ${showLinuxOptions ? 'rotate-180' : ''}`} />
        </button>
        
        {showLinuxOptions && (
            <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('download.linuxDistros')}
            </div>
            <a
              href={buildDownloadUrl(DOWNLOAD_FILES.linux.primary.file)}
              className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50"
              onClick={() =>
                trackConversion('download', {
                  source: 'smart-linux',
                  os: 'linux',
                  format: 'deb',
                })
              }
            >
              <span>{t('download.linuxFormatDeb')}</span>
              <span className="text-sm">{t('download.linuxDebian')}</span>
            </a>
            <a
              href={buildDownloadUrl(DOWNLOAD_FILES.linux.alternatives[0].file)}
              className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50"
              onClick={() =>
                trackConversion('download', {
                  source: 'smart-linux',
                  os: 'linux',
                  format: 'rpm',
                })
              }
            >
              <span>{t('download.linuxFormatRpm')}</span>
              <span className="text-sm">{t('download.linuxRpm')}</span>
            </a>
            <a
              href={buildDownloadUrl(DOWNLOAD_FILES.linux.alternatives[1].file)}
              className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-50"
              onClick={() =>
                trackConversion('download', {
                  source: 'smart-linux',
                  os: 'linux',
                  format: 'appimage',
                })
              }
            >
              <span>{t('download.linuxFormatAppImage')}</span>
              <span className="text-sm">{t('download.linuxUniversal')}</span>
            </a>
            <div className="border-t border-gray-200 my-1"></div>
            <a href={downloadPagePath} className="flex items-center gap-3 px-4 py-2 text-teal-600 hover:bg-teal-50 font-medium">
              <span>{t('download.allVersions')}</span>
              <span aria-hidden="true">→</span>
            </a>
          </div>
        )}
      </div>
    );
  }

  // Przycisk z opcją pokazania wszystkich wersji
  if (showAllVersions) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button 
          className={`${baseStyles} ${variantStyles} ${disabledStyles} ${className}`}
          onClick={() => setShowAllOptions(!showAllOptions)}
          style={buttonStyle}
        >
          {getIcon()}
          {getButtonText()}
          <ChevronDown className={`size-4 ml-1 transition-transform ${showAllOptions ? 'rotate-180' : ''}`} />
        </button>
        
        {showAllOptions && (
            <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
            <div className="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              {t('download.chooseVersion')}
            </div>
            {Object.entries(downloadAnchors).map(([key, url]) => (
              <a 
                key={key}
                href={url} 
                className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50"
              >
                {key === 'windows' ? (
                  <Monitor className="size-5" />
                ) : key === 'macos' ? (
                  <Apple className="size-5" />
                ) : (
                  <Laptop className="size-5" />
                )}
                <span>{downloadTitles[key]}</span>
                {os === key && (
                  <span className="ml-auto text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">
                    {t('download.recommended')}
                  </span>
                )}
              </a>
            ))}
            <div className="border-t border-gray-200 my-1"></div>
            <a 
              href={downloadPagePath} 
              className="flex items-center gap-3 px-4 py-2 text-teal-600 hover:bg-teal-50 font-medium text-sm"
            >
              <span>{t('download.seeAll')}</span>
              <span aria-hidden="true">→</span>
            </a>
          </div>
        )}
      </div>
    );
  }

  // Standardowy przycisk (Mac/Windows/Mobile)
  return (
    <button 
      className={`${baseStyles} ${variantStyles} ${disabledStyles} ${className}`}
      disabled={!isCompatible}
      style={buttonStyle}
      onClick={() => {
        if (!isCompatible) return;
        if (os === 'windows' || os === 'macos' || os === 'linux') {
          trackConversion('download', {
            source: 'smart',
            os,
          });
          window.location.href = getPrimaryDownloadUrl(os);
          return;
        }
        window.location.href = downloadPagePath;
      }}
    >
      {getIcon()}
      {getButtonText()}
    </button>
  );
}

export function DownloadSection() {
  const { t, i18n } = useTranslation();
  const { isCompatible } = useDeviceDetect();
  const currentLang = normalizeLanguage(i18n.language);
  const downloadPagePath = buildPath(currentLang, 'download');

  if (!isCompatible) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
        <Smartphone className="size-5 text-amber-600 mt-0.5" />
        <div>
          <p className="font-medium text-amber-900">{t('download.mobile')}</p>
          <p className="text-sm text-amber-700 mt-1">
            {t('download.mobileDescription')}
          </p>
          <a href={downloadPagePath} className="text-teal-600 hover:text-teal-700 text-sm font-medium mt-2 inline-block">
            <span>{t('download.learnMore')}</span>
            <span className="ml-1" aria-hidden="true">→</span>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <SmartDownloadButton variant="primary" />
      <a 
        href={downloadPagePath} 
        className="px-6 py-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 text-teal-600 hover:text-teal-700 border-2 border-teal-600 hover:bg-teal-50"
      >
        {t('download.allVersions')}
      </a>
    </div>
  );
}
